<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    cardBg?: string;
    headingHtml?: string;
    description?: string;
    ctaLabelHtml?: string;
    ctaFallbackText?: string;
    ctaHref?: string;
    ctaTarget?: string;
    /** BaseButton BgColor; empty = white pill (Figma). */
    ctaBg?: string;
    imageUrl?: string;
    imageSrcset?: string;
    imageAlt?: string;
    imageWidth?: number | null;
    imageHeight?: number | null;
  }>(),
  {
    cardBg: '',
    headingHtml: '',
    description: '',
    ctaLabelHtml: '',
    ctaFallbackText: '',
    ctaHref: '',
    ctaTarget: '_self',
    ctaBg: '',
    imageUrl: '',
    imageSrcset: '',
    imageAlt: '',
    imageWidth: null,
    imageHeight: null,
  },
);

const ctaLabelUsesHtml = computed(() => {
  const raw = props.ctaLabelHtml ?? '';
  return raw.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;
});

const cardStyle = computed(() => {
  const bg = props.cardBg?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
});

const ctaStyle = computed(() => {
  const bg = props.ctaBg?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
});

const show = computed(() => {
  const h = props.headingHtml ?? '';
  const t = h.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  const d = props.description?.trim() ?? '';
  const img = props.imageUrl?.trim() ?? '';
  const cta = props.ctaHref?.trim() ?? '';
  return t.length > 0 || d.length > 0 || img.length > 0 || cta.length > 0;
});

/** Bg image URL only; position split mobile (full-bleed) vs `lg:` in markup. */
const imageBackgroundStyle = computed((): Record<string, string> | undefined => {
  const url = props.imageUrl?.trim();
  if (!url) return undefined;
  const safe = url.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return {
    backgroundImage: `url("${safe}")`,
    backgroundRepeat: 'no-repeat',
  };
});
</script>

<template>
  <section
    v-if="show"
    class="hiw-last-section font-inter lg:mb-28 lg:mt-90 mt-70 mb-28"
  >
    <div class="container">
      <div
        class="overflow-hidden rounded-2xl shadow-sm md:rounded-[28px] lg:rounded-[36px]"
        :style="cardStyle"
      >
        <!--
          Mobile: fixed 500×343-style card; portrait anchored bottom-center on card bg.
          Desktop: 480px-tall 2-col grid; image fills right column.
        -->
        <div
          class="relative max-lg:h-[500px] max-lg:min-h-[500px] max-lg:max-h-[500px] lg:grid lg:h-[480px] lg:min-h-[480px] lg:max-h-[480px] lg:grid-cols-2 lg:grid-rows-[auto_1fr] lg:items-stretch lg:gap-x-12 lg:gap-y-0 lg:overflow-hidden lg:p-0 xl:gap-x-16"
        >
          <div
            v-if="imageUrl"
            class="max-lg:absolute max-lg:inset-0 max-lg:z-0 lg:relative lg:z-auto lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:h-full lg:min-h-0 lg:overflow-hidden lg:rounded-none"
          >
            <div
              class="absolute inset-0 bg-no-repeat motion-reduce:scale-100 max-lg:origin-bottom max-lg:scale-[1.04] max-lg:bg-[length:auto_118%] max-lg:bg-[position:10%_-15%] lg:bg-cover lg:origin-[center_50%] lg:scale-[1.12] lg:bg-[position:center_50%]"
              :style="imageBackgroundStyle"
              role="img"
              :aria-label="imageAlt?.trim() || 'Section illustration'"
            />
          </div>

          <div
            class="relative z-10 flex min-h-0 flex-col max-lg:max-w-[min(100%,340px)] max-lg:px-28 max-lg:pt-28 max-lg:[text-shadow:0_1px_3px_rgba(0,0,0,0.35)] lg:col-start-1 lg:row-start-1 lg:max-w-xl lg:pl-40 lg:pr-12 lg:pt-40 lg:[text-shadow:none] xl:pr-16"
          >
            <div
              v-if="headingHtml"
              class="leading-115 text-30 font-semibold tracking-tight text-white lg:text-40 lg:leading-110 [&_p]:m-0 [&_p]:leading-[inherit]"
              v-html="headingHtml"
            />
            <p
              v-if="description?.trim()"
              class="mt-12 whitespace-pre-line text-18 leading-150 text-white/95 lg:mt-24"
            >
              {{ description.trim() }}
            </p>
          </div>

          <a
            v-if="ctaHref"
            :href="ctaHref"
            :target="ctaTarget"
            class="hover:opacity-92 [&_*]:text-inherit relative z-20 inline-flex w-full items-center justify-center rounded-full px-48 py-16 text-center text-14 font-medium leading-none text-off-black-500 transition-opacity max-lg:absolute max-lg:inset-x-24 max-lg:bottom-28 max-lg:w-auto lg:col-start-1 lg:row-start-2 lg:mb-40 lg:ml-40 lg:mt-8 lg:w-fit lg:max-w-none lg:self-end [&_p]:m-0"
            :class="{ 'bg-white': !ctaBg?.trim() }"
            :style="ctaStyle"
          >
            <span
              v-if="ctaLabelUsesHtml"
              v-html="ctaLabelHtml"
            />
            <span v-else>{{ ctaFallbackText }}</span>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>
